/**
 * Everything the Email Queue screen shows and does: the rows, the counts, the
 * filters, and the actions that change them.
 *
 * Stats and bulk actions follow the customer, side, type and search filters but
 * not the status tab: each tab keeps its count while another is open, and "Cancel
 * pending" means what is pending whichever tab is showing.
 *
 * @link ./EmailQueueDashboard for the screen it drives.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  emailQueueAPI,
  type EmailQueueEntry,
  type EmailQueueFilterOptions,
  type EmailQueueStats,
} from '../../lib/api/emailQueue';
import { toast } from 'sonner';

export const EMAIL_QUEUE_PAGE_SIZE = 25;

type Params = Record<string, string | number | boolean>;

const NO_OPTIONS: EmailQueueFilterOptions = { types: [], customers: [], sides: [] };

/**
 * What the server said, else our own words: never an HTTP status for a person.
 *
 * @param err - the caught error.
 * @param fallback - the sentence to show when the server gave no reason.
 * @returns the message to display.
 */
export const queueErrorMessage = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

const pageGone = (err: unknown): boolean =>
  (err as { response?: { status?: number } })?.response?.status === 404;

/**
 * @param enabled - false for anyone but Helix staff, who get no requests at all.
 * @returns the queue's state, its filters and its actions.
 */
export function useEmailQueue(enabled: boolean) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [stats, setStats] = useState<EmailQueueStats | null>(null);
  const [entries, setEntries] = useState<EmailQueueEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [options, setOptions] = useState<EmailQueueFilterOptions>(NO_OPTIONS);
  const [page, setPage] = useState(1);

  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [sideFilter, setSideFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A slower, older request must not overwrite the answer to a newer one.
  const latestRequest = useRef(0);
  // One message for a failure that lasts, not one every ten seconds.
  const failureShown = useRef(false);

  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  /** What the stats, bulk actions and export share. */
  const scope = useMemo(() => {
    const params: Params = {};
    if (customerFilter !== 'all') params['customer'] = customerFilter;
    if (sideFilter !== 'all') params['side'] = sideFilter;
    if (typeFilter !== 'all') params['type'] = typeFilter;
    if (search) params['search'] = search;
    return params;
  }, [customerFilter, search, sideFilter, typeFilter]);

  const listFilters = useMemo(() => {
    const params: Params = { ...scope };
    if (statusFilter !== 'all') params['status'] = statusFilter;
    return params;
  }, [scope, statusFilter]);

  const fetchData = useCallback(async (isRefresh = false) => {
    const request = ++latestRequest.current;
    setRefreshing(true);
    try {
      const [statsRes, entriesRes] = await Promise.all([
        emailQueueAPI.getStats(scope),
        emailQueueAPI.getEntries({
          ...listFilters,
          page,
          page_size: EMAIL_QUEUE_PAGE_SIZE,
          ordering: '-created_at',
        }),
      ]);
      if (request !== latestRequest.current) return;
      setStats(statsRes.data);
      setEntries(entriesRes.data.results);
      setTotalCount(entriesRes.data.count);
      setLoadFailed(false);
      failureShown.current = false;
    } catch (err: unknown) {
      if (request !== latestRequest.current) return;
      // A cancel or a poll can leave this page past the end of a shorter list.
      if (pageGone(err) && page > 1) {
        setPage(1);
        return;
      }
      setLoadFailed(true);
      // A new view that failed must not sit over the previous view's rows.
      if (!isRefresh) {
        setEntries([]);
        setTotalCount(0);
        setStats(null);
      }
      if (!failureShown.current) toast.error(queueErrorMessage(err, 'Failed to load email queue data'));
      failureShown.current = true;
    } finally {
      if (request === latestRequest.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [listFilters, page, scope]);

  // What an action refreshes with: the filters may have moved while it ran.
  const latestFetch = useRef(fetchData);
  useEffect(() => {
    latestFetch.current = fetchData;
  }, [fetchData]);

  useEffect(() => {
    if (!enabled) return;
    fetchData();
  }, [enabled, fetchData]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!enabled) return undefined;
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, [enabled, fetchData]);

  // On filter changes only: the options do not need the ten-second refresh.
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    emailQueueAPI.getFilterOptions(listFilters)
      .then((response) => { if (live) setOptions(response.data); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [enabled, listFilters]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  }, []);

  /** Wraps a filter's setter so every change goes back to page 1. */
  const filterSetter = (set: (value: string) => void) => (value: string) => {
    set(value);
    setPage(1);
  };

  const run = async (action: () => Promise<unknown>, done: string, failed: string) => {
    try {
      await action();
      toast.success(done);
      await latestFetch.current(true);
    } catch (err: unknown) {
      toast.error(queueErrorMessage(err, failed));
    }
  };

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    await run(() => emailQueueAPI.cancelEntry(id), 'Email cancelled', 'Failed to cancel email');
    setCancellingId(null);
  };

  const handleRetry = async (id: number) => {
    setRetryingId(id);
    await run(() => emailQueueAPI.retryEntry(id), 'Email queued for retry', 'Failed to retry email');
    setRetryingId(null);
  };

  const handleCancelAll = async () => {
    setBulkActionLoading(true);
    await run(() => emailQueueAPI.cancelAll(scope), 'Pending emails cancelled', 'Failed to cancel emails');
    setBulkActionLoading(false);
  };

  const handleRetryAll = async () => {
    setBulkActionLoading(true);
    await run(() => emailQueueAPI.retryAll(scope), 'Failed emails queued for retry', 'Failed to retry emails');
    setBulkActionLoading(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await emailQueueAPI.exportEntries(listFilters);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-queue-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (err: unknown) {
      toast.error(queueErrorMessage(err, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return {
    loading,
    refreshing,
    loadFailed,
    stats,
    entries,
    totalCount,
    options,
    page,
    setPage,
    totalPages: Math.max(1, Math.ceil(totalCount / EMAIL_QUEUE_PAGE_SIZE)),
    statusFilter,
    setStatusFilter: filterSetter(setStatusFilter),
    typeFilter,
    setTypeFilter: filterSetter(setTypeFilter),
    customerFilter,
    setCustomerFilter: filterSetter(setCustomerFilter),
    sideFilter,
    setSideFilter: filterSetter(setSideFilter),
    searchInput,
    handleSearchChange,
    /** A filter other than the status tab is narrowing the queue. */
    scoped: Object.keys(scope).length > 0,
    /** Any filter at all, the status tab included. */
    filtered: Object.keys(listFilters).length > 0,
    cancellingId,
    retryingId,
    bulkActionLoading,
    exporting,
    refresh: () => fetchData(true),
    handleCancel,
    handleRetry,
    handleCancelAll,
    handleRetryAll,
    handleExport,
  };
}
